import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Footer } from './Footer';
import { MobileNav } from './MobileNav';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useAuthStore } from '../stores/authStore';
import { useSettingsStore } from '../stores/settingsStore';
import { usePortfoliosStore } from '../stores/portfoliosStore';
import { DisclaimerModal } from '../components/common/DisclaimerModal';
import { DisclaimerStrip } from '../components/common/DisclaimerStrip';
import { TopLoadingBar } from '../components/common/TopLoadingBar';
import { useLoadingStore, selectIsLoading } from '../stores/loadingStore';

/**
 * Routes that involve capital-at-risk decisions. The trading disclaimer only
 * gates these; read-only pages (Dashboard/Chart/Sentiment/Settings/System)
 * are accessible without accepting.
 */
const TRADING_ROUTE_PREFIXES = [
  '/trading',
  '/automated',
  '/journal',
  '/strategy',
  '/backtest',
  '/wallet',
  '/api-keys',
];

function isTradingRoute(pathname: string): boolean {
  return TRADING_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

export function AppShell() {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const disclaimerAcceptedAt = useSettingsStore((s) => s.disclaimerAcceptedAt);
  const acceptDisclaimer = useSettingsStore((s) => s.acceptDisclaimer);
  const refreshPortfolios = usePortfoliosStore((s) => s.refresh);
  const resetPortfolios = usePortfoliosStore((s) => s.reset);
  const isLoading = useLoadingStore(selectIsLoading);
  // Privacy ("streamer") mode masks USD via a module-level flag the pure
  // formatters read at call time (see tradeMetrics.ts). Components don't all
  // subscribe to it, so toggling it bumps this key to remount the whole shell
  // subtree — the bulletproof way to re-run every masked formatter at once.
  // The toggle is infrequent (start/stop a stream), so a remount is fine.
  const privacyMode = useSettingsStore((s) => s.privacyMode);
  const { pathname } = useLocation();

  // Keep the portfolios store in sync with auth state so the gate + the
  // header switcher always reflect the current user's portfolios.
  useEffect(() => {
    if (isAuthenticated) void refreshPortfolios();
    else resetPortfolios();
  }, [isAuthenticated, refreshPortfolios, resetPortfolios]);

  const showDisclaimer =
    isAuthenticated && !disclaimerAcceptedAt && isTradingRoute(pathname);

  return (
    <div
      key={privacyMode ? 'privacy-on' : 'privacy-off'}
      className="flex flex-col h-screen bg-bg-primary text-text-primary"
    >
      {/* Header — always visible */}
      <Header />

      <div className="relative flex flex-1 overflow-hidden">
        {/* Thin top loading bar — sits flush under the header, overlays the
            content row without shifting layout. Replaces the old full-page
            skeleton: navigations keep the current page visible. */}
        <TopLoadingBar active={isLoading} />

        {/* Sidebar — hidden on mobile */}
        {!isMobile && <Sidebar />}

        {/* Main content */}
        <main className="flex-1 min-w-0 min-h-0 overflow-x-hidden overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* Footer ticker bar — hidden on mobile */}
      {!isMobile && <Footer />}

      {/* Mobile bottom nav */}
      {isMobile && <MobileNav />}

      {/* Persistent disclaimer strip — always visible on desktop as a legal
          footnote below the ticker. Public, authenticated, trading, or not —
          the copy is accurate on every page. The explicit-accept modal is
          still gated separately (authed + trading route + not-yet-accepted). */}
      {!isMobile && <DisclaimerStrip />}

      <DisclaimerModal
        open={showDisclaimer}
        acceptedAt={disclaimerAcceptedAt}
        onAccept={() => acceptDisclaimer()}
      />
    </div>
  );
}
